import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload, Search, FileText, Download, Archive, Calendar, User,
  FolderOpen, Tag, Filter, Grid, List, FlaskConical, Pill,
  ClipboardList, TestTube, BookOpen, Video, ImageIcon, FileSpreadsheet,
  Star, X
} from 'lucide-react';

const VAULT_CATEGORIES = [
  { key: 'intake', label: 'Intake', icon: FileText, color: '#C89B7B' },
  { key: 'lab_report', label: 'Labs', icon: TestTube, color: '#7BADC8' },
  { key: 'mri_scans', label: 'MRI / Scans', icon: ImageIcon, color: '#9B7BC8' },
  { key: 'medication_schedule', label: 'Medication', icon: Pill, color: '#E07B7B' },
  { key: 'protocol', label: 'Protocol', icon: ClipboardList, color: '#C8A87B' },
  { key: 'case_history', label: 'Case History', icon: FolderOpen, color: '#4E7A6B' },
  { key: 'predecode', label: 'PreDECODE', icon: FlaskConical, color: '#7BA3C8' },
  { key: 'decode', label: 'DECODE', icon: FlaskConical, color: '#E07B7B' },
  { key: 'twenty_day_report', label: 'Reports', icon: FileSpreadsheet, color: '#C87BAD' },
  { key: 'video', label: 'Videos', icon: Video, color: '#7BC8A8' },
  { key: 'educational', label: 'Educational', icon: BookOpen, color: '#C87BAD' },
  { key: 'content', label: 'Other', icon: FileText, color: '#888' },
];

const TAG_OPTIONS = [
  { key: 'used_in_decode', label: 'Used in DECODE', color: 'border-red-500/30 text-red-400 bg-red-500/8' },
  { key: 'used_in_protocol', label: 'Used in Protocol', color: 'border-amber-500/30 text-amber-400 bg-amber-500/8' },
  { key: 'important', label: 'Important', color: 'border-emerald-500/30 text-emerald-400 bg-emerald-500/8' },
];

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [subscriberFilter, setSubscriberFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({ subscriber_id: '', document_type: 'intake', title: '', description: '', tags: [] });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [docsRes, usersRes] = await Promise.all([
        axios.get(`${API}/documents`),
        axios.get(`${API}/users?role=subscriber`),
      ]);
      setDocuments(docsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !uploadForm.subscriber_id || !uploadForm.title) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('subscriber_id', uploadForm.subscriber_id);
      formData.append('document_type', uploadForm.document_type);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      if (uploadForm.tags.length > 0) formData.append('tags', JSON.stringify(uploadForm.tags));
      await axios.post(`${API}/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setShowUploadDialog(false);
      setUploadForm({ subscriber_id: '', document_type: 'intake', title: '', description: '', tags: [] });
      setSelectedFile(null);
      fetchData();
    } catch (err) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const handleArchive = async (docId) => {
    try {
      await axios.patch(`${API}/documents/${docId}/archive`);
      toast.success('Document archived');
      fetchData();
    } catch (err) { toast.error('Failed to archive'); }
  };

  const handleTag = async (docId, tag) => {
    try {
      await axios.patch(`${API}/documents/${docId}/tags`, { tag, action: 'toggle' });
      fetchData();
    } catch (err) { toast.error('Failed to update tag'); }
  };

  const filtered = documents.filter(doc => {
    const matchSearch = !searchTerm || doc.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = typeFilter === 'all' || doc.document_type === typeFilter;
    const matchSub = subscriberFilter === 'all' || doc.subscriber_id === subscriberFilter;
    return matchSearch && matchType && matchSub;
  });

  // Group by subscriber for vault view
  const groupedBySub = {};
  filtered.forEach(doc => {
    const sid = doc.subscriber_id || 'unassigned';
    if (!groupedBySub[sid]) groupedBySub[sid] = [];
    groupedBySub[sid].push(doc);
  });

  const getCategoryMeta = (type) => VAULT_CATEGORIES.find(c => c.key === type) || VAULT_CATEGORIES[VAULT_CATEGORIES.length - 1];

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto" data-testid="documents-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-adm-primary font-['Work_Sans'] tracking-tight" data-testid="documents-title">
              Source Document Vault
            </h1>
            <p className="text-adm-tertiary text-xs mt-1">Per-subscriber document management with categories and tagging</p>
          </div>
          <Button onClick={() => setShowUploadDialog(true)} className="bg-adm-accent/15 text-adm-accent border-0 hover:bg-adm-accent/25 text-xs" data-testid="upload-document-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Document
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-adm-tertiary" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search documents..."
              className="pl-8 bg-adm-panel border-white/[0.06] text-adm-primary text-xs h-8" data-testid="doc-search" />
          </div>
          <Select value={subscriberFilter} onValueChange={setSubscriberFilter}>
            <SelectTrigger className="w-44 bg-adm-panel border-white/[0.06] text-adm-secondary text-xs h-8" data-testid="subscriber-filter">
              <User className="w-3 h-3 mr-1" />
              <SelectValue placeholder="All Subscribers" />
            </SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-adm-primary text-xs">All Subscribers</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id} className="text-adm-primary text-xs">{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-36 bg-adm-panel border-white/[0.06] text-adm-secondary text-xs h-8" data-testid="type-filter">
              <FolderOpen className="w-3 h-3 mr-1" />
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              <SelectItem value="all" className="text-adm-primary text-xs">All Categories</SelectItem>
              {VAULT_CATEGORIES.map(c => (
                <SelectItem key={c.key} value={c.key} className="text-adm-primary text-xs">{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center border border-white/[0.06] rounded-md overflow-hidden">
            <button onClick={() => setViewMode('list')} className={`p-1.5 ${viewMode === 'list' ? 'bg-adm-panel text-adm-primary' : 'text-adm-tertiary'}`}>
              <List className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-1.5 ${viewMode === 'grid' ? 'bg-adm-panel text-adm-primary' : 'text-adm-tertiary'}`}>
              <Grid className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Category folder quick access */}
        {subscriberFilter !== 'all' && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="category-folders">
            {VAULT_CATEGORIES.map(cat => {
              const count = filtered.filter(d => d.document_type === cat.key).length;
              return (
                <button key={cat.key} onClick={() => setTypeFilter(typeFilter === cat.key ? 'all' : cat.key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] transition-colors border ${
                    typeFilter === cat.key ? 'border-white/15 bg-adm-panel' : 'border-white/[0.04] bg-adm-bg/30 hover:bg-adm-bg/50'
                  }`}>
                  <cat.icon className="w-3 h-3" style={{ color: cat.color }} />
                  <span className="text-adm-secondary">{cat.label}</span>
                  {count > 0 && <span className="text-adm-accent font-mono">{count}</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Documents */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-adm-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-adm-panel border-white/[0.06] p-10 text-center">
            <FolderOpen className="w-8 h-8 text-adm-tertiary mx-auto mb-2" strokeWidth={1} />
            <p className="text-adm-secondary text-sm">No documents found</p>
            <p className="text-adm-tertiary text-xs mt-1">Upload documents to build the subscriber vault</p>
          </Card>
        ) : viewMode === 'list' ? (
          <div className="space-y-1.5" data-testid="doc-list">
            {filtered.map(doc => {
              const cat = getCategoryMeta(doc.document_type);
              const Icon = cat.icon;
              const sub = users.find(u => u.id === doc.subscriber_id);
              const tags = doc.tags || [];
              return (
                <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg bg-adm-panel border border-white/[0.04] hover:border-white/[0.08] transition-colors" data-testid={`doc-${doc.id}`}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-medium text-adm-primary truncate">{doc.title}</span>
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-white/10 text-adm-tertiary">{cat.label}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-adm-tertiary">
                      <span>{sub?.name || 'Unknown'}</span>
                      <span className="flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    {/* Tags */}
                    {tags.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {tags.map(t => {
                          const tagMeta = TAG_OPTIONS.find(to => to.key === t);
                          return tagMeta ? (
                            <Badge key={t} variant="outline" className={`text-[8px] px-1 py-0 ${tagMeta.color}`}>{tagMeta.label}</Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {TAG_OPTIONS.map(to => (
                      <button key={to.key} onClick={() => handleTag(doc.id, to.key)} title={to.label}
                        className={`p-1 rounded transition-colors ${tags.includes(to.key) ? 'opacity-100' : 'opacity-25 hover:opacity-50'}`}>
                        {to.key === 'important' ? <Star className="w-3 h-3 text-emerald-400" /> : <Tag className="w-3 h-3 text-amber-400" />}
                      </button>
                    ))}
                    <Button variant="ghost" size="icon" onClick={() => window.open(`${API}/documents/${doc.id}/download`, '_blank')}
                      className="text-adm-tertiary hover:text-adm-primary h-7 w-7">
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleArchive(doc.id)}
                      className="text-adm-tertiary hover:text-red-400 h-7 w-7">
                      <Archive className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="doc-grid">
            {filtered.map(doc => {
              const cat = getCategoryMeta(doc.document_type);
              const Icon = cat.icon;
              return (
                <Card key={doc.id} className="bg-adm-panel border-white/[0.04] hover:border-white/[0.08] transition-colors cursor-pointer" data-testid={`doc-grid-${doc.id}`}>
                  <CardContent className="p-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${cat.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: cat.color }} />
                    </div>
                    <p className="text-xs font-medium text-adm-primary truncate">{doc.title}</p>
                    <p className="text-[10px] text-adm-tertiary mt-0.5">{cat.label}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <Button variant="ghost" size="icon" onClick={() => window.open(`${API}/documents/${doc.id}/download`, '_blank')}
                        className="text-adm-tertiary hover:text-adm-primary h-6 w-6">
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleArchive(doc.id)}
                        className="text-adm-tertiary hover:text-red-400 h-6 w-6">
                        <Archive className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="bg-adm-surface border-white/[0.06] max-w-md" data-testid="upload-dialog">
            <DialogHeader>
              <DialogTitle className="text-adm-primary text-sm font-['Work_Sans']">Upload to Document Vault</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-3 mt-2">
              <div>
                <Label className="text-adm-secondary text-xs">Subscriber</Label>
                <Select value={uploadForm.subscriber_id} onValueChange={v => setUploadForm(prev => ({ ...prev, subscriber_id: v }))}>
                  <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary text-xs" data-testid="upload-subscriber">
                    <SelectValue placeholder="Select subscriber" />
                  </SelectTrigger>
                  <SelectContent className="bg-adm-panel border-white/10">
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id} className="text-adm-primary text-xs">{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-adm-secondary text-xs">Category</Label>
                <Select value={uploadForm.document_type} onValueChange={v => setUploadForm(prev => ({ ...prev, document_type: v }))}>
                  <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary text-xs" data-testid="upload-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-adm-panel border-white/10">
                    {VAULT_CATEGORIES.map(c => (
                      <SelectItem key={c.key} value={c.key} className="text-adm-primary text-xs">{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-adm-secondary text-xs">Title</Label>
                <Input value={uploadForm.title} onChange={e => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-adm-bg border-white/10 text-adm-primary text-xs" required data-testid="upload-title" />
              </div>
              <div>
                <Label className="text-adm-secondary text-xs">Notes</Label>
                <Textarea value={uploadForm.description} onChange={e => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2} />
              </div>
              <div>
                <Label className="text-adm-secondary text-xs">Tags</Label>
                <div className="flex gap-2 mt-1">
                  {TAG_OPTIONS.map(t => (
                    <button key={t.key} type="button" onClick={() => {
                      setUploadForm(prev => ({
                        ...prev,
                        tags: prev.tags.includes(t.key) ? prev.tags.filter(x => x !== t.key) : [...prev.tags, t.key]
                      }));
                    }}
                      className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                        uploadForm.tags.includes(t.key) ? t.color : 'border-white/10 text-adm-tertiary'
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-adm-secondary text-xs">File</Label>
                <Input type="file" onChange={e => setSelectedFile(e.target.files?.[0])}
                  className="bg-adm-bg border-white/10 text-adm-primary text-xs" required data-testid="upload-file" />
                <p className="text-[10px] text-adm-tertiary mt-1">Supports PDF, Image, DOCX, Spreadsheet, Video</p>
              </div>
              <Button type="submit" disabled={uploading || !uploadForm.subscriber_id || !uploadForm.title}
                className="w-full bg-adm-accent/15 text-adm-accent border-0 hover:bg-adm-accent/25 text-xs" data-testid="submit-upload">
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
